import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useLang } from '@/contexts/LanguageContext';
import { useAuth } from '@/contexts/AuthContext';
import { useGravityToast } from '@/components/effects/GravityToast';
import api from '@/utils/api';
import { RankBadge } from '@/utils/ranks';
import ScrollReveal from '@/components/effects/ScrollReveal';
import { ArrowLeft, Calendar, Clock, Tag, MessageCircle, Share2, Copy } from 'lucide-react';

export default function JournalPost() {
  const { slug } = useParams();
  const { t, localizedField } = useLang();
  const { user } = useAuth();
  const toast = useGravityToast();
  const [post, setPost] = useState(null);
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api.get(`/journal/${slug}`).then(r => {
      setPost(r.data);
      setLoading(false);
      if (r.data.allow_comments) {
        api.get(`/comments/journal/${r.data.id}`).then(cr => setComments(cr.data)).catch(() => {});
      }
    }).catch(() => setLoading(false));
  }, [slug]);

  const handleComment = async (e) => {
    e.preventDefault();
    if (!newComment.trim()) return;
    try {
      const { data } = await api.post('/comments', {
        target_type: 'journal', target_id: post.id, content: newComment
      });
      setComments(prev => [...prev, data]);
      setNewComment('');
      toast.success('Comment posted');
    } catch { toast.error('Failed to post comment'); }
  };

  const copyLink = () => {
    navigator.clipboard.writeText(window.location.href);
    toast.info('Link copied!');
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center"><div className="text-sacred-gold animate-gold-pulse">Loading...</div></div>;
  if (!post) return <div className="min-h-screen flex items-center justify-center"><p className="text-text-muted">{t('common.notFound')}</p></div>;

  const content = localizedField(post.content);
  const wordCount = typeof content === 'string' ? content.split(/\s+/).length : 0;
  const readTime = Math.max(1, Math.ceil(wordCount / 200));

  return (
    <div data-testid="journal-post-page" className="min-h-screen py-20 md:py-32 px-4">
      <div className="max-w-4xl mx-auto">
        <ScrollReveal>
          <Link to="/journal" className="inline-flex items-center gap-2 text-sacred-gold font-body text-sm mb-8 hover:text-molten-gold transition-colors">
            <ArrowLeft size={16} /> {t('journal.title')}
          </Link>
        </ScrollReveal>

        {post.header_image_url && (
          <ScrollReveal>
            <div className="w-full aspect-video rounded-2xl overflow-hidden mb-10 gold-glow">
              <img src={post.header_image_url.startsWith('/') ? `${process.env.REACT_APP_BACKEND_URL}${post.header_image_url}` : post.header_image_url} alt="" className="w-full h-full object-cover" />
            </div>
          </ScrollReveal>
        )}

        <ScrollReveal>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-display text-heading mb-4" data-testid="post-title">
            {localizedField(post.title)}
          </h1>
          <div className="flex flex-wrap items-center gap-4 text-text-muted/70 text-sm font-body mb-10">
            {post.published_at && (
              <span className="flex items-center gap-1"><Calendar size={14} /> {new Date(post.published_at).toLocaleDateString()}</span>
            )}
            <span className="flex items-center gap-1"><Clock size={14} /> {readTime} {t('journal.readTime')}</span>
            {post.tags?.map(tag => (
              <span key={tag} className="flex items-center gap-1 px-2 py-0.5 rounded-full bg-sacred-gold/10 text-sacred-gold text-xs">
                <Tag size={10} /> {tag}
              </span>
            ))}
          </div>
        </ScrollReveal>

        <ScrollReveal>
          <div className="prose-gravity mb-16" data-testid="post-content">
            {typeof content === 'string' ? (
              <div dangerouslySetInnerHTML={{ __html: content.replace(/\n/g, '<br/>') }} />
            ) : (
              <p className="text-deep-brown/80 font-body">{JSON.stringify(content)}</p>
            )}
          </div>
        </ScrollReveal>

        <div className="flex items-center gap-3 mb-16">
          <span className="text-text-muted/70 text-sm font-body">{t('journal.share')}:</span>
          <button onClick={copyLink} className="glass-button p-2 rounded-full" data-testid="share-copy"><Copy size={16} /></button>
          <a href={`https://twitter.com/intent/tweet?url=${encodeURIComponent(window.location.href)}&text=${encodeURIComponent(localizedField(post.title))}`} target="_blank" rel="noopener noreferrer" className="glass-button p-2 rounded-full"><Share2 size={16} /></a>
        </div>

        {post.allow_comments && (
          <section data-testid="comments-section">
            <h2 className="text-2xl font-display text-heading mb-6 flex items-center gap-2">
              <MessageCircle size={22} className="text-sacred-gold" /> {t('journal.comments')} ({comments.length})
            </h2>

            {user && (
              <form onSubmit={handleComment} className="mb-8">
                <textarea
                  value={newComment}
                  onChange={e => setNewComment(e.target.value)}
                  placeholder={t('journal.leaveComment')}
                  rows={3}
                  className="w-full px-4 py-3 rounded-xl glass-input font-body text-sm resize-none mb-3"
                  data-testid="comment-input"
                />
                <button type="submit" className="gold-button px-5 py-2 rounded-full font-body text-sm" data-testid="comment-submit">
                  {t('journal.submit')}
                </button>
              </form>
            )}

            <div className="space-y-4">
              {comments.map(c => (
                <div key={c.id} className="glass-card rounded-xl p-4" data-testid={`comment-${c.id}`}>
                  <div className="flex items-center gap-3 mb-2">
                    <div className="w-8 h-8 rounded-full bg-sacred-gold/10 flex items-center justify-center text-sacred-gold text-xs font-bold">
                      {c.user?.display_name?.[0]?.toUpperCase() || '?'}
                    </div>
                    <div>
                      <Link to={`/profile/${c.user?.username}`} className="text-deep-brown font-body text-sm hover:text-sacred-gold">
                        {c.user?.display_name || 'Anonymous'}
                      </Link>
                      {c.user?.rank && <RankBadge rank={c.user.rank} size="sm" />}
                    </div>
                    <span className="text-text-muted/50 text-xs font-body ml-auto">{new Date(c.created_at).toLocaleDateString()}</span>
                  </div>
                  <p className="text-deep-brown/80 font-body text-sm pl-11">{c.content}</p>
                </div>
              ))}
            </div>
          </section>
        )}
      </div>
    </div>
  );
}
